/** One-line node installers served by Brunel Command; values are quoted so nothing is interpreted by the shell. */
export interface NodeConnection { url: string; publishableKey: string }

const posix = (value: string) => `'${value.replaceAll("'", "'\\''")}'`;
const powershell = (value: string) => `'${value.replaceAll("'", "''")}'`;

/**
 * Text placed on a subscriber-visible settings surface. The real command still
 * carries the service address for copy and download; this copy does not.
 */
export function hideServiceAddress(text: string, serviceAddress: string): string {
  const address = serviceAddress.trim();
  if (address.length === 0) return text;
  return text.replaceAll(address, 'configured service');
}

export function installCommands(origin: string, code: string, connection: NodeConnection, name = ''): { windows: string; mac: string } {
  const base = origin.replace(/\/+$/, '');
  const trimmed = name.trim();
  return {
    windows: `& ([scriptblock]::Create((irm ${base}/install.ps1))) -Origin ${powershell(base)} -Code ${powershell(code)} -Url ${powershell(connection.url)} -Key ${powershell(connection.publishableKey)}${trimmed ? ` -Name ${powershell(trimmed)}` : ''}`,
    mac: `curl -fsSL ${base}/install.sh | sh -s -- --origin ${posix(base)} --code ${posix(code)} --url ${posix(connection.url)} --key ${posix(connection.publishableKey)}${trimmed ? ` --name ${posix(trimmed)}` : ''}`,
  };
}

/**
 * A double-click installer for Windows: a .cmd file that runs the same PowerShell
 * installer with this node's one-time code already filled in.
 */
export function windowsInstallerFile(origin: string, code: string, connection: NodeConnection, name: string): { fileName: string; content: string } {
  const values = [origin, code, connection.url, connection.publishableKey, name];
  // cmd.exe treats these specially even inside the double-quoted -Command argument.
  if (values.some((value) => /["%\r\n]/.test(value))) throw new Error('these values cannot be written into an installer file');
  const command = installCommands(origin, code, connection, name).windows;
  const safeName = name.trim().replace(/[^A-Za-z0-9 ._-]/g, '').trim() || 'this computer';
  const content = [
    '@echo off',
    `title Brunel Node setup - ${safeName}`,
    'echo Setting up Brunel Node. This window closes when it is done.',
    `powershell -NoProfile -ExecutionPolicy Bypass -Command "${command}"`,
    'if errorlevel 1 (echo. & echo Setup did not finish. & pause & exit /b 1)',
    'echo. & echo Done. Brunel Node now runs in the background.',
    'timeout /t 10',
    '',
  ].join('\r\n');
  return { fileName: `Brunel Node Setup - ${safeName}.cmd`, content };
}

/** The machine kinds a node can be put on, in the node's own vocabulary. */
export const NODE_HOST_KINDS = ['windows', 'macos', 'linux', 'nas', 'inference_server'] as const;
export type NodeHostKind = (typeof NODE_HOST_KINDS)[number];

const HOST_LABELS: Readonly<Record<NodeHostKind, string>> = {
  windows: 'a Windows PC',
  macos: 'a Mac',
  linux: 'a Linux computer',
  nas: 'a NAS (a Synology or similar always-on box)',
  inference_server: 'a machine kept for running AI models',
};

const HOST_NOTES: Readonly<Record<NodeHostKind, string>> = {
  windows: 'The installer fetches Node.js through winget if it is missing.',
  macos: 'Node.js 22 or newer must already be on PATH; install it first if it is not.',
  linux: 'Node.js 22 or newer must already be on PATH; install it with the system package manager if it is not.',
  nas: 'Node.js 22 or newer must be on PATH. On a NAS that usually means a container rather than a package, and the installer will refuse without it. If you cannot get Node.js 22 there, stop and say so rather than working around it.',
  inference_server: 'Node.js 22 or newer must already be on PATH; install it with the system package manager if it is not.',
};

/**
 * A brief the owner can paste into an AI agent running on the machine that is to
 * become a node, so nobody has to follow install steps by hand. It carries the
 * one-time code, so it is as sensitive as the code itself and expires with it.
 */
export function agentBrief(
  origin: string, code: string, connection: NodeConnection,
  name: string, hostKind: NodeHostKind, expiresAt: string,
): string {
  const base = origin.replace(/\/+$/, '');
  const trimmed = name.trim();
  const commands = installCommands(base, code, connection, trimmed);
  const command = hostKind === 'windows' ? commands.windows : commands.mac;
  const shell = hostKind === 'windows' ? 'PowerShell' : 'a terminal';
  const expiry = Number.isNaN(Date.parse(expiresAt)) ? 'shortly' : new Date(expiresAt).toISOString();
  return [
    `You are working on ${HOST_LABELS[hostKind]}. Put a Brunel node on this computer, so it can`,
    'take tasks from Brunel Command. Work on THIS computer only.',
    '',
    `1. Check the prerequisite. ${HOST_NOTES[hostKind]}`,
    `2. Open ${shell} as the person who will be logged in day to day - not as an administrator`,
    '   or root - and run exactly this, as one line:',
    '',
    command,
    '',
    '3. The installer sets this computer up on its own: it finds the AI tools and models already',
    '   installed here, registers the computer under the name it is given, and leaves the node',
    '   running in the background so it starts again after a reboot. It asks nothing.',
    `4. It has worked when the installer exits without an error and ${trimmed || 'this computer'} appears`,
    `   as connected in Brunel Command (${base}) within a minute.`,
    '',
    'Rules:',
    `- The code in that command works once and stops working at ${expiry}. Do not put it in a file,`,
    '  a commit, a chat message or anywhere else.',
    '- The key in that command is a publishable key. Never replace it with a secret or service key,',
    '  and never send either to anything but the address already in the command.',
    '- Do not edit the installer, and do not reproduce what you think it does by hand. If it fails,',
    '  report what it printed and stop.',
  ].join('\n');
}
